/* External */
import {
  memo, useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import InteractiveElement from '@dtvgo/interactive-element';

import { getCommunicationCarousel } from '../../services/communicationCarousel';

import withInteractiveFocusable from '../../hocs/withInteractiveFocusable.hoc';
import useImpressionOnVisible from '../../hooks/useImpressionOnVisible';
import { useGtmLocalContext } from '../../providers/GtmLocalProvider/GtmLocalProvider';
import {
  GTM_CATEGORIES,
  GTM_SUBCATEGORIES,
  GTM_EVENT_NAMES,
  getParentLander,
  getTagEventFromRoute,
} from '../../utils/googleTags';

import { mapHomeBannerPayload } from './mapHomeBannerPayload';
import { navigateHomeBannerCta } from './navigateHomeBannerCta';

/* Utils */
import { useIsSidebarExpanded, useOpenSidebar } from '../../providers/SidebarStateContext';
import useOnLeftArrowPress from '../../hooks/useOnLeftArrowPress';

/* Other */
import { selectShowModal } from '../../storage/modal/selectors';

import './HomeBannerSlot.scss';

const PLACEHOLDER_ASSET = { __homeBannerPlaceholder: true };

const homeBannerPropTypes = {
  banner: PropTypes.shape({
    title: PropTypes.string,
    logoUrl: PropTypes.string,
    backgroundUrl: PropTypes.string,
    backgroundColor: PropTypes.string,
    ctaText: PropTypes.string,
    ctaLink: PropTypes.string,
  }).isRequired,
};

function HomeBannerSlotFocusableInner({
  banner,
  focused,
  onMouseDown = undefined,
  logoFailed,
  onLogoError,
  className = '',
  stealFocusByDefault = false,
  setFocus = undefined,
  domRef = undefined,
}) {
  const isSidebarExpanded = useIsSidebarExpanded();
  const isModalShown = useSelector(selectShowModal) || false;
  const hasAttemptedFocusStealRef = useRef(false);

  useEffect(() => {
    if (!stealFocusByDefault || hasAttemptedFocusStealRef.current) {
      return;
    }

    if (isSidebarExpanded || isModalShown) {
      return;
    }

    hasAttemptedFocusStealRef.current = true;
    setFocus?.();
  }, [stealFocusByDefault, isSidebarExpanded, isModalShown, setFocus]);

  const hasImage = Boolean(banner.backgroundUrl);
  const hasLogo = Boolean(banner.logoUrl) && !logoFailed;
  const showCtaLabel = Boolean(banner.ctaText);

  const containerStyle = {};
  if (hasImage) {
    containerStyle.backgroundImage = `url(${banner.backgroundUrl})`;
  } else if (banner.backgroundColor) {
    containerStyle.backgroundColor = banner.backgroundColor;
  }

  const sectionClass = [
    'dtv-html5-c-home-banner-slot',
    hasImage ? 'dtv-html5-c-home-banner-slot--with-image' : 'dtv-html5-c-home-banner-slot--solid',
    focused ? 'dtv-html5-c-home-banner-slot--focused' : '',
    className,
  ].filter(Boolean).join(' ');

  const contentClass = [
    'dtv-html5-c-home-banner-slot__content',
    hasImage ? 'dtv-html5-c-home-banner-slot__content--with-image' : 'dtv-html5-c-home-banner-slot__content--solid',
  ].join(' ');

  return (
    <InteractiveElement
      ref={domRef}
      tag="section"
      className={sectionClass}
      style={containerStyle}
      onMouseDown={onMouseDown}
      aria-label={banner.title || 'Home banner'}
    >
      {hasImage && <div className="dtv-html5-c-home-banner-slot__overlay" aria-hidden="true" />}
      <div className={contentClass}>
        {hasLogo && (
          <img
            src={banner.logoUrl}
            alt=""
            className="dtv-html5-c-home-banner-slot__logo"
            onError={onLogoError}
          />
        )}
        {banner.title && <h3 className="dtv-html5-c-home-banner-slot__title">{banner.title}</h3>}
        {showCtaLabel && (
          <span className="dtv-html5-c-home-banner-slot__cta-button">{banner.ctaText}</span>
        )}
      </div>
    </InteractiveElement>
  );
}

HomeBannerSlotFocusableInner.propTypes = {
  ...homeBannerPropTypes,
  focused: PropTypes.bool.isRequired,
  onMouseDown: PropTypes.func,
  logoFailed: PropTypes.bool.isRequired,
  onLogoError: PropTypes.func.isRequired,
  className: PropTypes.string,
  stealFocusByDefault: PropTypes.bool,
  setFocus: PropTypes.func,
  domRef: PropTypes.func,
};

const HomeBannerSlotFocusable = memo(
  withInteractiveFocusable()(memo(HomeBannerSlotFocusableInner)),
);

function HomeBannerSlot({
  carouselId,
  focusKey = undefined,
  onFetched,
  onUnavailable = undefined,
  fetchEnabled = true,
  onBecameFocused = undefined,
  stealFocusByDefault = false,
}) {
  const intl = useIntl();
  const { commonTaggingEvent } = useGtmLocalContext();
  const goToSideBar = useOpenSidebar();
  const handleLeftArrowPress = useOnLeftArrowPress(goToSideBar);
  const { pathname } = useLocation();
  const tagFromRoute = getTagEventFromRoute({ path: pathname });
  const landerTab = getParentLander(tagFromRoute) || tagFromRoute;
  const onFetchedRef = useRef(onFetched);
  const onUnavailableRef = useRef(onUnavailable);
  const [banner, setBanner] = useState();
  const [fetchResolved, setFetchResolved] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  onFetchedRef.current = onFetched;
  onUnavailableRef.current = onUnavailable;

  const handleLogoError = useCallback(() => {
    setLogoFailed(true);
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!fetchEnabled) {
      queueMicrotask(() => {
        if (!cancelled) onFetchedRef.current([PLACEHOLDER_ASSET]);
      });
      return () => {
        cancelled = true;
      };
    }

    setFetchResolved(false);

    (async () => {
      const raw = await getCommunicationCarousel({ carouselId });
      if (cancelled) return;
      const mapped = mapHomeBannerPayload(raw);
      setBanner(mapped);
      setFetchResolved(true);
      if (!mapped && String(carouselId || '').trim()) {
        onUnavailableRef.current?.(carouselId);
      }
      onFetchedRef.current([PLACEHOLDER_ASSET]);
    })();

    return () => {
      cancelled = true;
    };
  }, [carouselId, fetchEnabled]);

  const bannerRef = useImpressionOnVisible({
    enabled: Boolean(banner),
    resetKey: carouselId,
    onImpression: () => {
      commonTaggingEvent({
        category: GTM_CATEGORIES.HOME_DATA,
        subCategory: GTM_SUBCATEGORIES.PAGES.BANNER,
        eventName: GTM_EVENT_NAMES.HOME_BANNER.PAGE,
        data: { bannerTitle: banner.title, tab: landerTab },
      });
    },
  });

  const handleEnterPress = useCallback(() => {
    if (!banner?.ctaLink) return;
    commonTaggingEvent({
      category: GTM_CATEGORIES.HOME_DATA,
      subCategory: GTM_SUBCATEGORIES.PAGES.BANNER,
      eventName: GTM_EVENT_NAMES.HOME_BANNER.BANNER_CLICK,
      data: { bannerTitle: banner?.title, tab: landerTab },
    });
    navigateHomeBannerCta(banner.ctaLink);
  }, [banner, commonTaggingEvent, landerTab]);

  if (!fetchEnabled) {
    // eslint-disable-next-line unicorn/no-null -- React requires null for empty render
    return null;
  }

  if (!fetchResolved && String(carouselId || '').trim()) {
    return (
      <section
        className="dtv-html5-c-home-banner-slot dtv-html5-c-home-banner-slot--solid dtv-html5-c-home-banner-slot--placeholder"
        aria-busy="true"
        aria-label={intl.formatMessage({ id: 'common.loadingCommunication' })}
      />
    );
  }

  if (!banner) {
    // eslint-disable-next-line unicorn/no-null -- React requires null for empty render
    return null;
  }

  const resolvedFocusKey = focusKey || `home-banner-${carouselId}`;

  return (
    <HomeBannerSlotFocusable
      focusKey={resolvedFocusKey}
      onEnterPress={handleEnterPress}
      onArrowPress={handleLeftArrowPress}
      onBecameFocused={onBecameFocused}
      banner={banner}
      logoFailed={logoFailed}
      onLogoError={handleLogoError}
      stealFocusByDefault={stealFocusByDefault}
      domRef={bannerRef}
    />
  );
}

HomeBannerSlot.propTypes = {
  carouselId: PropTypes.string.isRequired,
  focusKey: PropTypes.string,
  onFetched: PropTypes.func.isRequired,
  onUnavailable: PropTypes.func,
  fetchEnabled: PropTypes.bool,
  onBecameFocused: PropTypes.func,
  stealFocusByDefault: PropTypes.bool,
};

export default memo(HomeBannerSlot);
